/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ElementRef, Injectable, OnDestroy, Renderer2, RendererFactory2, inject } from '@angular/core';

import { InternalFeaturesConfig } from '@frontend/sports/common/client-config-data-access';

@Injectable({ providedIn: 'root' })
export class AutomationService implements OnDestroy {
    private internalFeatureConfig = inject(InternalFeaturesConfig);

    get isActive(): boolean {
        return this.internalFeatureConfig.isAutomationDebugEnabled;
    }

    constructor() {
        const rendererFactory = inject(RendererFactory2);

        this.renderer = rendererFactory.createRenderer(null, null);
    }

    private readonly attributeName = 'data-automation';
    private renderer: Renderer2;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private destroy = () => {};

    private removeAllAttributes(elementRef: ElementRef): void {
        const element = elementRef.nativeElement as Element;
        const attributes = Object.keys(element.attributes)

            .map((key) => {
                const attribute = element.attributes[key as keyof NamedNodeMap] as object;
                if ('name' in attribute) {
                    return attribute.name as string;
                }
                return '';
            })

            .filter((n) => n.startsWith(this.attributeName));

        for (const attr of attributes) {
            this.renderer.removeAttribute(element, attr);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addAttr(elementRef: ElementRef, value: string | { [key: string]: any } | null | undefined): void {
        if (!this.isActive) {
            return;
        }

        this.removeAllAttributes(elementRef);

        if (value == null || typeof value === 'string') {
            // @ts-ignore No idea why there is a typescript error here.
            this.setSimpleAttribute(elementRef, value);
        } else {
            this.setExtendedAttribute(elementRef, value);
        }

        this.destroy = () => this.removeAllAttributes(elementRef);
    }

    private setSimpleAttribute(elementRef: ElementRef, value: string | null | undefined): void {
        if (typeof value === 'undefined') {
            this.renderer.setAttribute(elementRef.nativeElement, this.attributeName, 'undefined');

            return;
        }

        if (value == null) {
            this.renderer.setAttribute(elementRef.nativeElement, this.attributeName, 'null');

            return;
        }

        this.renderer.setAttribute(elementRef.nativeElement, this.attributeName, value);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setExtendedAttribute(elementRef: ElementRef, value: { [key: string]: any }): void {
        const keys = Object.keys(value);

        for (const key of keys) {
            this.renderer.setAttribute(elementRef.nativeElement, `${this.attributeName}-${key}`, value[key]);
        }
    }

    ngOnDestroy(): void {
        this.destroy();
    }
}
